document.addEventListener("DOMContentLoaded", function () {
  // Default scenarios
  const DEFAULT_SCENARIOS = [
    { name: "Conservative", trafficUplift: 0.1, cvrChange: 0 },
    { name: "Moderate", trafficUplift: 0.25, cvrChange: 0 },
    { name: "Optimistic", trafficUplift: 0.2, cvrChange: 0.003 },
    { name: "Aggressive", trafficUplift: 0.5, cvrChange: 0.003 },
  ]

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat(window.supercoMarket === "us" ? "en-US" : "en-GB", {
      style: "currency",
      currency: window.supercoMarket === "us" ? "USD" : "GBP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  // Format number
  const formatNumber = (value) => {
    return new Intl.NumberFormat(window.supercoMarket === "us" ? "en-US" : "en-GB").format(Math.round(value))
  }

  // Format percent
  const formatPercent = (value) => {
    return `${(value * 100).toFixed(1)}%`
  }

  // Initialize a single calculator instance
  const initCalculator = (section) => {
    const sectionId = section.dataset.sectionId
    // Use the section itself as the calculator root.
    // The markup doesn't include a `.seo-calculator__content` wrapper, so
    // querying for it would return null and prevent any calculations.
    const calculator = section

    // Get all input elements
    const inputs = {
      baselineSessions: calculator.querySelector(
        '[data-input="baselineSessions"]'
      ),
      baselineCvr: calculator.querySelector('[data-input="baselineCvr"]'),
      aov: calculator.querySelector('[data-input="aov"]'),
      contributionMargin: calculator.querySelector(
        '[data-input="contributionMargin"]'
      ),
      seoRetainer: calculator.querySelector('[data-input="seoRetainer"]'),
    }

    // Helper function to parse input value, allowing empty strings
    const parseInputValue = (input) => {
      const value = input.value.trim()
      if (value === "") return { value: 0, isEmpty: true }
      const parsed = parseFloat(value)
      if (isNaN(parsed) || parsed < 0) {
        return { value: 0, isEmpty: false, isInvalid: true }
      }
      return { value: parsed, isEmpty: false, isInvalid: false }
    }

    // Calculate and update all values
    const calculate = () => {
      const baselineSessionsData = parseInputValue(inputs.baselineSessions)
      const baselineCvrData = parseInputValue(inputs.baselineCvr)
      const aovData = parseInputValue(inputs.aov)
      const contributionMarginData = parseInputValue(inputs.contributionMargin)
      const seoRetainerData = parseInputValue(inputs.seoRetainer)

      // Use 0 for calculations, but only update input field if it's invalid (negative), not if empty
      let baselineSessions = baselineSessionsData.value
      let baselineCvr = baselineCvrData.value
      let aov = aovData.value
      let contributionMargin = contributionMarginData.value
      let seoRetainer = seoRetainerData.value

      // Only update input fields if they have invalid (negative) values, not if they're empty
      if (baselineSessionsData.isInvalid && !baselineSessionsData.isEmpty) {
        inputs.baselineSessions.value = 0
      }
      if (baselineCvrData.isInvalid && !baselineCvrData.isEmpty) {
        inputs.baselineCvr.value = 0
      }
      if (aovData.isInvalid && !aovData.isEmpty) {
        inputs.aov.value = 0
      }
      if (contributionMarginData.isInvalid && !contributionMarginData.isEmpty) {
        inputs.contributionMargin.value = 0
      }
      if (seoRetainerData.isInvalid && !seoRetainerData.isEmpty) {
        inputs.seoRetainer.value = 0
      }

      // Convert percentage inputs to decimals after clamping
      baselineCvr = baselineCvr / 100
      contributionMargin = contributionMargin / 100

      // Baseline calculations
      const baselineMonthlyRevenue = baselineSessions * baselineCvr * aov
      const baselineAnnualRevenue = baselineMonthlyRevenue * 12
      const annualSeoCost = seoRetainer * 12

      // Update baseline outputs
      const baselineMonthlyEl = calculator.querySelector(
        '[data-output="baselineMonthlyRevenue"]'
      )
      const baselineAnnualEl = calculator.querySelector(
        '[data-output="baselineAnnualRevenue"]'
      )
      const annualSeoCostEl = calculator.querySelector(
        '[data-output="annualSeoCost"]'
      )

      if (baselineMonthlyEl)
        baselineMonthlyEl.textContent = formatCurrency(baselineMonthlyRevenue)
      if (baselineAnnualEl)
        baselineAnnualEl.textContent = formatCurrency(baselineAnnualRevenue)
      if (annualSeoCostEl)
        annualSeoCostEl.textContent = formatCurrency(annualSeoCost)

      // Calculate scenarios
      const scenarioResults = DEFAULT_SCENARIOS.map((scenario) => {
        const newSessions = Math.round(
          baselineSessions * (1 + scenario.trafficUplift)
        )
        const newCvr = baselineCvr + scenario.cvrChange
        const newMonthlyRevenue = newSessions * newCvr * aov
        const incrementalMonthlyRevenue =
          newMonthlyRevenue - baselineMonthlyRevenue
        const incrementalAnnualRevenue = incrementalMonthlyRevenue * 12
        const incrementalAnnualProfit =
          incrementalAnnualRevenue * contributionMargin - annualSeoCost
        const roi =
          annualSeoCost > 0 ? incrementalAnnualProfit / annualSeoCost : 0

        return {
          name: scenario.name,
          trafficUplift: scenario.trafficUplift,
          cvrChange: scenario.cvrChange,
          newSessions,
          newCvr,
          newMonthlyRevenue,
          incrementalMonthlyRevenue,
          incrementalAnnualRevenue,
          incrementalAnnualProfit,
          annualSeoCost,
          roi,
        }
      })

      // Find best scenario
      const bestScenario = scenarioResults.reduce((best, current) =>
        current.roi > best.roi ? current : best
      )

      // Update best scenario outputs
      const bestRoiEl = calculator.querySelector('[data-output="bestRoi"]')
      const bestNameEl = calculator.querySelector(
        '[data-output="bestScenarioName"]'
      )
      const bestRevenueEl = calculator.querySelector(
        '[data-output="bestIncrementalRevenue"]'
      )
      const bestProfitEl = calculator.querySelector(
        '[data-output="bestIncrementalProfit"]'
      )
      const bestSessionsEl = calculator.querySelector(
        '[data-output="bestNewSessions"]'
      )

      if (bestRoiEl) bestRoiEl.textContent = `${bestScenario.roi.toFixed(1)}x`
      if (bestNameEl) bestNameEl.textContent = bestScenario.name
      if (bestRevenueEl)
        bestRevenueEl.textContent = formatCurrency(
          bestScenario.incrementalAnnualRevenue
        )
      if (bestProfitEl)
        bestProfitEl.textContent = formatCurrency(
          bestScenario.incrementalAnnualProfit
        )
      if (bestSessionsEl)
        bestSessionsEl.textContent = formatNumber(bestScenario.newSessions)

      // Update table
      const tableBody = calculator.querySelector(".seo-calculator__table tbody")
      if (tableBody) {
        tableBody.innerHTML = scenarioResults
          .map((scenario) => {
            const isHighlighted = scenario.name === bestScenario.name
            const roiClass =
              scenario.roi >= 5
                ? "text-accent-green"
                : scenario.roi >= 2
                ? "text-primary-white"
                : "text-gray-400"

            return `
              <tr class="${
                isHighlighted ? "seo-calculator__row--highlighted" : ""
              }">
                <td class="seo-calculator__cell--name">${scenario.name}</td>
                <td>${formatPercent(scenario.trafficUplift)}</td>
                <td>${
                  scenario.cvrChange > 0
                    ? `+${(scenario.cvrChange * 100).toFixed(1)}pp`
                    : "-"
                }</td>
                <td>${formatNumber(scenario.newSessions)}</td>
                <td>${formatPercent(scenario.newCvr)}</td>
                <td>${formatCurrency(scenario.newMonthlyRevenue)}</td>
                 <td class="text-accent-green">${formatCurrency(
                   scenario.incrementalMonthlyRevenue
                 )}</td>
                 <td class="text-accent-green">${formatCurrency(
                   scenario.incrementalAnnualRevenue
                 )}</td>
                 <td class="text-accent-green font-semibold">${formatCurrency(
                   scenario.incrementalAnnualProfit
                 )}</td>
                 <td class="text-gray-400">${formatCurrency(
                   scenario.annualSeoCost
                 )}</td>
                <td class="seo-calculator__cell--roi ${roiClass}">${scenario.roi.toFixed(
              1
            )}x</td>
              </tr>
            `
          })
          .join("")
      }
    }

    // Handle blur event to enforce minimum value when field loses focus
    const handleBlur = (input) => {
      const value = input.value.trim()
      if (value === "" || isNaN(parseFloat(value)) || parseFloat(value) < 0) {
        input.value = 0
        calculate()
      }
    }

    // Add event listeners to all inputs
    Object.values(inputs).forEach((input) => {
      if (input) {
        input.addEventListener("input", calculate)
        input.addEventListener("change", calculate)
        input.addEventListener("blur", () => handleBlur(input))
      }
    })

    // Initial calculation
    calculate()
  }

  // Initialize all calculator sections on the page
  const calculatorSections = document.querySelectorAll(
    ".seo-investment-calculator"
  )
  calculatorSections.forEach(initCalculator)
})
